import React, { useEffect, useState } from 'react';

import { styled } from 'styled-components';

import { useSound } from 'use-sound';

import { _useDispatch, _useSelector } from '@src/state/hooks';
import { popEventQueue } from '@src/state/slices/slice_game';
import { getCurrentEvent } from '@src/state/selectors/selector_game';

import action from '@src/asset/sound/action.wav';
import error from '@src/asset/sound/error.wav';
import sanko from '@src/asset/sound/sanko.wav';
import lowTime from '@src/asset/sound/lowTime.wav';
import yourTurnSound from '@src/asset/sound/yourTurn.wav';
import gameOver from '@src/asset/sound/gameOver.wav';
import gameWin from '@src/asset/sound/gameWin.wav';
import drewCard from '@src/asset/sound/drewCard.wav';

const ToastContainer = styled.div<{
  color: string;
  textColor?: string;
}>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  flex: 1;
  align-self: center;

  cursor: pointer;

  margin: 10pt;
  // margin-top: 5rem;
  margin-bottom: 50vh;
  padding: 10pt;

  width: 30%;
  min-height: 7rem;

  text-align: center;

  border: 2pt solid black;

  font-size: 24pt;

  color: ${(props) => {
    if (props.textColor) return props.textColor;
    return props.color === 'white' ? 'black' : 'white';
  }};

  z-index: 101;

  background-color: ${(props) => props.color};
  
  @keyframes fadeIn {
    0% { opacity: 0; }
    100% { opacity: 1; }
  }

  animation: fadeIn 1s;
  -webkit-animation: fadeIn 1s;

  @keyframes fadeOut {
    0% { opacity: 1; }
    100% { opacity: 0; }
  }

  // drop shadow
  box-shadow: 0pt 4pt 4pt 0 rgba(0, 0, 0, 0.5);

  @media only screen and (max-width: 1280px) {
    width: 70%;
    margin-bottom: 50vh;
  }

  a {
    padding: 4pt;
    font-size: 3rem;
    cursor: pointer;

    border: 2pt solid gray;

    &:hover {
      color: #ff0000;
    }
  }
`;

type Props = {
  timeout: number;
  textColor?: string;
  gameColor: string | null;
};

export default function Toast(props: Props) {
  const dispatch = _useDispatch();
  const currentEvent = _useSelector(getCurrentEvent);

  const [showToast, setShowToast] = useState(false);
  const [playedSound, setPlayedSound] = useState(false);

  const [playError, errorInfo] = useSound(error, { volume: 0.4 });
  const [playAction, actionInfo] = useSound(action, { volume: 0.4 });
  const [playSanko, sankoInfo] = useSound(sanko, { volume: 0.4 });
  const [playLowTime, lowTimeInfo] = useSound(lowTime, { volume: 0.4 });
  const [playYourTurnSound, yourTurnSoundInfo] = useSound(yourTurnSound, {
    volume: 0.4,
  });
  const [playGameEndSound, gameEndSoundInfo] = useSound(gameOver, {
    volume: 0.4,
  });
  const [playGameWinSound, gameWinSoundInfo] = useSound(gameWin, {
    volume: 0.4,
  });
  const [playDrewCardSound, drewCardSoundInfo] = useSound(drewCard, {
    volume: 0.4,
  });

  useEffect(() => {
    let t1: number;
    if (currentEvent) {
      setPlayedSound(false);
      setShowToast(true);

      t1 = setTimeout(() => {
        dispatch(popEventQueue());
        setShowToast(false);
      }, props.timeout);
    } else {
      setShowToast(false);
    }

    return () => {
      clearTimeout(t1);
    };
  }, [currentEvent]);

  useEffect(() => {
    if (currentEvent?.sound && !playedSound) {
      if (playedSound) return;
      if (!errorInfo.duration) return;

      // play sound
      if (currentEvent.sound != null) {
        switch (currentEvent.sound) {
          case 'error':
            playError();
            break;
          case 'action':
            playAction();
            break;
          case 'sanko':
            playSanko();
            break;
          case 'lowtime':
            playLowTime();
            break;
          case 'yourTurn':
            playYourTurnSound();
            break;
          case 'gameOver':
            playGameEndSound();
            break;
          case 'gameWin':
            playGameWinSound();
            break;
          case 'drewCard':
            playDrewCardSound();
            break;
        }

        setPlayedSound(true);
      }
    }
  }, [
    currentEvent,
    errorInfo,
    actionInfo,
    sankoInfo,
    lowTimeInfo,
    yourTurnSoundInfo,
    gameEndSoundInfo,
    gameWinSoundInfo,
    drewCardSoundInfo,
  ]);

  if (showToast) {
    return (
      <ToastContainer
        color={props.gameColor || 'black'}
        textColor={props.textColor}
        onClick={() => dispatch(popEventQueue())}
      >
        {currentEvent?.message}
      </ToastContainer>
    );
  } else {
    return null;
  }
}
